import React, { type FC, useEffect, useContext, useState, type PropsWithChildren } from 'react';

import { getI18nData, isBrowser } from '@lucid/utils';

import type { Lucid } from '../../types/types';
import LayoutContext from '../context/layout-context';
import Icon, { type IconTypes } from '../icon/icon';

interface ShareIconsProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The i18n translation data. */
  i18nData?: Lucid.I18n.DataEdges;
}

const icons = [
  {
    key: 'twitter',
    bgColor: '#000000',
    href: 'https://twitter.com/share?url=',
    icon: 'Twitter',
  },
  {
    key: 'linkedin',
    bgColor: '#0077B5',
    href: 'https://linkedin.com/shareArticle?url=',
    icon: 'Linkedin',
  },
  {
    key: 'facebook',
    bgColor: '#1877F2',
    href: 'https://www.facebook.com/sharer/sharer.php?u=',
    icon: 'Facebook',
  },
];

const ShareIcons: FC<PropsWithChildren<ShareIconsProps>> = ({ className, i18nData, ...rest }) => {
  const [url, setUrl] = useState('');
  const { shortCode } = useContext(LayoutContext);
  const i18n = getI18nData(i18nData, shortCode);

  useEffect(() => {
    if (isBrowser()) {
      setUrl(window.location.href);
    }
  }, []);

  return (
    <aside
      {...rest}
      css={theme => ({
        label: 'shareIcons',
        animation: `0.75s ease-in-out ${theme.keyframe.slideFromRight}`,
        position: 'fixed',
        right: theme.space.s4,
        top: '0',
        height: 'calc(100vh - 65px)',
        zIndex: theme.zIndices[2],
        display: 'flex',
        gap: theme.space.s4,
        flexDirection: 'column',
        justifyContent: 'center',
        [theme.breakpoints.bp1220.min]: {
          top: '65px',
        },
      })}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
    >
      {icons.map(({ key, bgColor, href, icon }) => (
        <a
          key={key}
          aria-label={i18n('mktg-share-icon-aria-label', { application: icon })}
          css={theme => ({
            backgroundColor: bgColor,
            borderRadius: '50%',
            height: '25px',
            padding: '2px 4px 4px 4px',
            width: '25px',
            marginBottom: theme.space.s8,
            [theme.breakpoints.bp1220.min]: {
              height: '46px',
              width: '46px',
              padding: theme.space.s8,
              marginBottom: '0',
            },
          })}
          href={`${href}${url}`}
          rel="noreferrer"
          target="_blank"
        >
          <Icon color="white" sizing="responsive" type={icon as IconTypes} />
        </a>
      ))}
    </aside>
  );
};

export default ShareIcons;
